import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Radio, Alert, Spin, Empty } from 'antd';
import {
  ApiOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import adminApi from '../../services/tenantService';
import { SystemApiStats } from '../../types/tenant';

const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

const AdminSystemPage: React.FC = () => {
  const [stats, setStats] = useState<SystemApiStats | null>(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getSystemApiStats(days);
      setStats(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const totalCalls = stats?.daily.reduce((sum, d) => sum + Number(d.totalCalls || 0), 0) || 0;
  const totalErrors = stats?.daily.reduce((sum, d) => sum + Number(d.errorCount || 0), 0) || 0;
  const avgResponseTime = stats?.daily.length
    ? Math.round(stats.daily.reduce((sum, d) => sum + Number(d.avgResponseTime || 0), 0) / stats.daily.length)
    : 0;
  const errorRate = totalCalls > 0 ? ((totalErrors / totalCalls) * 100).toFixed(2) : '0.00';

  const dailyColumns: ColumnsType<SystemApiStats['daily'][0]> = [
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d: string) => dateFormat.format(new Date(d)) },
    { title: 'Calls', dataIndex: 'totalCalls', key: 'calls', align: 'right', render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</span> },
    { title: 'Avg Time', dataIndex: 'avgResponseTime', key: 'avg', align: 'right', render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}ms</span> },
    { title: 'P95 Time', dataIndex: 'p95ResponseTime', key: 'p95', align: 'right', render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}ms</span> },
    {
      title: 'Errors', dataIndex: 'errorCount', key: 'errors', align: 'right',
      render: (v: number) => v > 0 ? <span style={{ color: '#C62828' }}>{v}</span> : <span style={{ color: '#999' }}>0</span>,
    },
    { title: 'Error Rate', dataIndex: 'errorRate', key: 'errorRate', align: 'right', render: (v: number) => `${v}%` },
  ];

  const slowColumns: ColumnsType<SystemApiStats['slowEndpoints'][0]> = [
    {
      title: 'Endpoint', key: 'endpoint',
      render: (_, e) => <span><Tag>{e.method}</Tag> <code style={{ fontSize: 13 }}>{e.path}</code></span>,
    },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', width: 80, align: 'right' },
    { title: 'Avg', dataIndex: 'avgResponseTime', key: 'avg', width: 100, align: 'right', render: (v: number) => <span style={{ color: '#F57C00', fontWeight: 500 }}>{v}ms</span> },
    { title: 'Max', dataIndex: 'maxResponseTime', key: 'max', width: 100, align: 'right', render: (v: number) => <span style={{ color: '#C62828' }}>{v}ms</span> },
  ];

  const tenantColumns: ColumnsType<SystemApiStats['topTenants'][0]> = [
    {
      title: '#', key: 'rank', width: 40,
      render: (_v: unknown, _r: SystemApiStats['topTenants'][0], i: number) => <span style={{ color: '#999' }}>{i + 1}</span>,
    },
    { title: 'Company', dataIndex: 'companyName', key: 'name' },
    { title: 'Calls', dataIndex: 'totalCalls', key: 'calls', align: 'right', render: (v: number) => v.toLocaleString() },
    { title: 'Avg Time', dataIndex: 'avgResponseTime', key: 'avg', align: 'right', render: (v: number) => `${v}ms` },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Radio.Group value={days} onChange={(e) => setDays(e.target.value)} buttonStyle="solid">
          <Radio.Button value={7}>7d</Radio.Button>
          <Radio.Button value={14}>14d</Radio.Button>
          <Radio.Button value={30}>30d</Radio.Button>
        </Radio.Group>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderLeft: '4px solid #1976D2' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Total API Calls" value={totalCalls} prefix={<ApiOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderLeft: '4px solid #2E7D32' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Avg Response Time" value={avgResponseTime} suffix="ms" prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderLeft: '4px solid #C62828' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Total Errors" value={totalErrors} prefix={<WarningOutlined />} valueStyle={{ color: totalErrors > 0 ? '#C62828' : undefined }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderLeft: '4px solid #F57C00' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Error Rate" value={errorRate} suffix="%" prefix={<ThunderboltOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Daily Traffic */}
      <Card title="Daily API Traffic" style={{ marginBottom: 24 }}>
        <Table
          columns={dailyColumns}
          dataSource={stats?.daily || []}
          rowKey="date"
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description="No data yet" /> }}
        />
      </Card>

      {/* Slow Endpoints */}
      <Card title="Slowest Endpoints (avg > 500ms)" style={{ marginBottom: 24 }}>
        <Table
          columns={slowColumns}
          dataSource={stats?.slowEndpoints || []}
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description="No slow endpoints detected" /> }}
        />
      </Card>

      {/* Top Tenants */}
      <Card title="Top Tenants by API Usage">
        <Table
          columns={tenantColumns}
          dataSource={stats?.topTenants || []}
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description="No usage data yet" /> }}
        />
      </Card>
    </div>
  );
};

export default AdminSystemPage;
